/**
 * size-chart.js
 *
 * Web Component <size-chart-popup>. Sin dependencias externas.
 * Lee los datos desde window.SIZE_CHART_DATA (size-chart-data.js), así que
 * ese script debe cargarse antes que este.
 *
 * Mejoras respecto a la versión anterior:
 * - Las tablas se construyen con el DOM (createElement/textContent) en vez de
 *   concatenar strings de HTML, así que es imposible dejar un <td> o <th> sin
 *   cerrar por error de transcripción.
 * - El selector de marca se genera desde los datos, no se mantiene a mano en
 *   el HTML — evita que una marca con datos cargados quede invisible en el
 *   dropdown (eso le pasaba a "Heron Preston" en la versión anterior).
 * - Modal accesible: foco atrapado dentro del diálogo mientras está abierto,
 *   Escape para cerrar, el foco vuelve al botón que lo abrió al cerrar,
 *   aria-modal/aria-labelledby, y el scroll del body se bloquea mientras
 *   está abierto.
 * - Usa un custom element para que el snippet pueda incluirse más de una vez
 *   en la misma página (por ejemplo en un quick-view) sin que los ids choquen.
 */
(function () {
  if (customElements.get('size-chart-popup')) return;

  const FOCUSABLE_SELECTOR =
    'button, [href], select, input, textarea, [tabindex]:not([tabindex="-1"])';

  class SizeChartPopup extends HTMLElement {
    connectedCallback() {
      this.trigger = this.querySelector('.size-chart__trigger');
      this.overlay = this.querySelector('.size-chart__overlay');
      this.dialog = this.querySelector('.size-chart__dialog');
      this.closeBtn = this.querySelector('.size-chart__close');
      this.brandSelect = this.querySelector('[data-size-chart-brand]');
      this.typeSelect = this.querySelector('[data-size-chart-type]');
      this.tablesContainer = this.querySelector('[data-size-chart-tables]');

      this._lastFocused = null;
      this._onKeydown = this._onKeydown.bind(this);

      this._populateBrands();

      this.trigger.addEventListener('click', () => this.open());
      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());
      this.brandSelect.addEventListener('change', () => this._renderTables());
      this.typeSelect.addEventListener('change', () => this._renderTables());

      this._renderTables();
    }

    _populateBrands() {
      const data = window.SIZE_CHART_DATA || {};
      const brands = Object.keys(data).sort((a, b) => a.localeCompare(b, 'es'));
      this.brandSelect.textContent = '';
      brands.forEach((brand) => {
        const option = document.createElement('option');
        option.value = brand;
        option.textContent = brand;
        this.brandSelect.appendChild(option);
      });
    }

    open() {
      this._lastFocused = document.activeElement;
      this.overlay.hidden = false;
      this.dialog.hidden = false;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
      document.addEventListener('keydown', this._onKeydown);
      (this.closeBtn || this.dialog).focus();
    }

    close() {
      this.overlay.hidden = true;
      this.dialog.hidden = true;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
      if (this._lastFocused) this._lastFocused.focus();
    }

    disconnectedCallback() {
      // A prueba de fallos: si el elemento se destruye (ej. el editor de temas
      // vuelve a renderizar la sección) mientras el modal está abierto, no debe
      // quedar el scroll de la página bloqueado para siempre.
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
      document.removeEventListener('keydown', this._onKeydown);
    }

    _onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key === 'Tab') {
        const focusable = Array.from(this.dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
          (el) => el.offsetParent !== null
        );
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    _renderTables() {
      const data = window.SIZE_CHART_DATA || {};
      const brand = this.brandSelect.value;
      const type = this.typeSelect.value;
      const subtables = (data[brand] && data[brand][type]) || [];

      this.tablesContainer.textContent = '';

      if (!subtables.length) {
        const empty = document.createElement('p');
        empty.className = 'size-chart__empty';
        empty.textContent =
          'Todavía no tenemos esta tabla cargada. Escríbenos por WhatsApp y te ayudamos con tu talla.';
        this.tablesContainer.appendChild(empty);
        return;
      }

      subtables.forEach((subtable) => {
        if (subtable.title) {
          const heading = document.createElement('h3');
          heading.className = 'size-chart__subtitle';
          heading.textContent = subtable.title;
          this.tablesContainer.appendChild(heading);
        }

        if (subtable.rows && subtable.rows.length) {
          this.tablesContainer.appendChild(this._buildTable(subtable.headers, subtable.rows));
        }

        if (subtable.note) {
          const note = document.createElement('p');
          note.className = 'size-chart__note';
          note.textContent = subtable.note;
          this.tablesContainer.appendChild(note);
        }
      });
    }

    _buildTable(headers, rows) {
      const wrapper = document.createElement('div');
      wrapper.className = 'size-chart__table-wrapper';

      const table = document.createElement('table');
      table.className = 'size-chart__table';

      const thead = document.createElement('thead');
      const headRow = document.createElement('tr');
      (headers || []).forEach((label) => {
        const th = document.createElement('th');
        th.scope = 'col';
        th.textContent = label;
        headRow.appendChild(th);
      });
      thead.appendChild(headRow);
      table.appendChild(thead);

      const tbody = document.createElement('tbody');
      rows.forEach((row) => {
        const tr = document.createElement('tr');
        row.forEach((cell, index) => {
          // La primera columna de cada fila se trata como encabezado de fila
          // (más accesible con lectores de pantalla en tablas anchas).
          const cellEl = document.createElement(index === 0 ? 'th' : 'td');
          if (index === 0) cellEl.scope = 'row';
          cellEl.textContent = cell;
          tr.appendChild(cellEl);
        });
        tbody.appendChild(tr);
      });
      table.appendChild(tbody);

      wrapper.appendChild(table);
      return wrapper;
    }
  }

  customElements.define('size-chart-popup', SizeChartPopup);
})();